import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'app/hooks/useToast';
import {
  CROP_PRESETS,
  SOCIAL_MEDIA_PRESETS_GROUPS,
} from './imageEditor/imageEditorConfig';
import style from './imageEditor/ImageEditor.module.scss';
import FilerobotImageEditor from 'react-filerobot-image-editor';
import Modal from 'components/shared/Modal';
import './imageEditor/filerobotImageEditor.scss';
import { useQuery } from 'react-query';
import { queryKey } from 'constants/queryKey';
import { ImageTemplatesApi } from 'api/imageTemplates';
import ImageTemplatesUtil from 'utils/imageTemplates';
import { v4 } from 'uuid';

const JPEG_QUALITY = 0.9;

function flattenCanvasOntoWhite(source: HTMLCanvasElement): HTMLCanvasElement {
  const output = document.createElement('canvas');
  output.width = source.width;
  output.height = source.height;

  const context = output.getContext('2d');
  if (!context) return source;

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, output.width, output.height);
  context.drawImage(source, 0, 0);

  return output;
}

type Props = {
  imageSrc: string;
  onClose(): void;
  onImageEdited(file: File): void;
  pageId?: number;
};

const ImageEditor: FC<Props> = (props) => {
  const toast = useToast();
  const { t } = useTranslation();
  const watermarks = useQuery(
    queryKey.pageImageTemplates(props.pageId),
    () => {
      if (!props.pageId) return Promise.resolve([]);

      return ImageTemplatesApi.get(props.pageId).then((res) =>
        ImageTemplatesUtil.formatToWatermarks(res),
      );
    },
    {
      enabled: typeof props.pageId === 'number',
    },
  );

  async function onSaveImage(result: { imageCanvas?: HTMLCanvasElement }) {
    if (!result.imageCanvas) return;

    try {
      const flattenedCanvas = flattenCanvasOntoWhite(result.imageCanvas);

      const blob = await new Promise<Blob | null>((resolve) =>
        flattenedCanvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
      );

      if (!blob) throw new Error('Failed to encode edited image');

      const file = new File([blob], `${v4()}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      props.onImageEdited(file);
      props.onClose();
    } catch (e) {
      toast(t('somethingWentWrong'), 'error');
    }
  }

  return (
    <Modal classNames={{ modal: style.modal }}>
      <FilerobotImageEditor
        source={props.imageSrc}
        onClose={props.onClose}
        onBeforeSave={() => false}
        onSave={onSaveImage}
        previewPixelRatio={2}
        savingPixelRatio={2}
        Crop={{
          presetsItems: CROP_PRESETS,
          presetsFolders: [
            {
              titleKey: 'socialMedia',
              groups: SOCIAL_MEDIA_PRESETS_GROUPS,
            },
          ],
        }}
        Watermark={
          watermarks.data
            ? { gallery: watermarks.data.map((watermark) => watermark.url) }
            : undefined
        }
      />
    </Modal>
  );
};

export default ImageEditor;
